import { useEffect, useState, useCallback } from "react";
import SuperAdminLayout from "./SuperAdminLayout";
import { fetchSecurityEvents, fetchAdminEvents } from "@/services/superadminApi";
import Loader from "@/components/ui/Loader";
import {
    SectionHeader,
    Badge,
    CardGlass,
    Button,
    Skeleton
} from "@/components/ui/PremiumComponents";
import {
    ShieldAlert,
    Zap,
    Search,
    Filter,
    Terminal,
    Clock,
    ChevronRight,
    Settings,
    Lock,
    UserCheck
} from 'lucide-react';

const TYPE_THEMES = {
    failed_login: 'danger',
    blocked_by_subscription: 'warning',
    invalid_subdomain: 'secondary',
    rate_limit_exceeded: 'warning',
    superadmin_action: 'success',
    create_tenant: 'success',
    set_plan: 'primary',
};

export default function SuperAdminEvents() {
    const [tab, setTab] = useState("security"); // security | admin
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [nextCursor, setNextCursor] = useState(null);
    const [filterType, setFilterType] = useState("");
    const [filterCompany, setFilterCompany] = useState("");

    const load = useCallback(
        async (reset = true) => {
            if (reset) setLoading(true);
            else setLoadingMore(true);

            const cursor = reset ? null : nextCursor;
            const params = { limit: 50 };
            if (cursor) params.cursor = cursor;
            if (filterCompany) params.company_id = filterCompany;

            try {
                let result;
                if (tab === "security") {
                    if (filterType) params.event_type = filterType;
                    result = await fetchSecurityEvents(params);
                } else {
                    if (filterType) params.action = filterType;
                    result = await fetchAdminEvents(params);
                }

                const newItems = result.items || [];
                setEvents((prev) => (reset ? newItems : [...prev, ...newItems]));
                setNextCursor(result.next_cursor || null);
            } catch {
                if (reset) setEvents([]);
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        },
        [tab, filterType, filterCompany, nextCursor]
    );

    useEffect(() => {
        load(true);
    }, [tab, filterType, filterCompany]);

    const typeOptions =
        tab === "security"
            ? ["failed_login", "blocked_by_subscription", "invalid_subdomain", "rate_limit_exceeded"]
            : ["create_tenant", "set_plan", "pause_tenant", "resume_tenant", "override_feature"];

    return (
        <SuperAdminLayout>
            <div className="page-fade-in">
                <SectionHeader
                    title="Auditoría de Eventos"
                    subtitle="Monitoreo crítico de seguridad y trazabilidad de acciones administrativas."
                    actions={
                        <div className="flex bg-white/5 p-1 rounded-2xl border border-white/10 gap-1">
                            {[
                                { id: "security", label: "Seguridad", icon: <Lock size={14} /> },
                                { id: "admin", label: "Operaciones", icon: <Settings size={14} /> },
                            ].map((t) => (
                                <button
                                    key={t.id}
                                    onClick={() => {
                                        setTab(t.id);
                                        setFilterType("");
                                        setEvents([]);
                                    }}
                                    className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-black transition-all ${tab === t.id
                                        ? 'bg-primary text-white shadow-lg shadow-primary/20'
                                        : 'hover:text-white hover:bg-white/5'
                                        }`}
                                    style={tab !== t.id ? { color: 'var(--text-soft)' } : {}}
                                >
                                    {t.icon} {t.label}
                                </button>
                            ))}
                        </div>
                    }
                />

                {/* Advanced Filters */}
                <div className="flex flex-col md:flex-row gap-4 mb-10 items-center">
                    <div className="relative flex-1 w-full group">
                        <Search
                            size={18}
                            className="absolute left-4 top-1/2 -translate-y-1/2 group-focus-within:text-primary transition-colors"
                            style={{ color: 'var(--text-muted)' }}
                        />
                        <input
                            type="text"
                            placeholder="Buscar por Company ID o Tenant..."
                            value={filterCompany}
                            onChange={(e) => setFilterCompany(e.target.value)}
                            className="w-full bg-white/5 border border-white/10 rounded-2xl py-3.5 pl-12 pr-4 text-sm text-white outline-none focus:ring-2 ring-primary/20 transition-all font-medium"
                        />
                    </div>
                    <div className="relative md:w-64">
                        <Filter size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="w-full bg-white/5 border border-white/10 rounded-2xl py-3.5 pl-12 pr-4 text-sm text-white outline-none cursor-pointer appearance-none font-bold"
                        >
                            <option value="">Todos los tipos</option>
                            {typeOptions.map((t) => (
                                <option key={t} value={t}>{t.replace('_', ' ')}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {loading ? (
                    <div className="space-y-4">
                        {[1, 2, 3, 4, 5].map(i => (
                            <CardGlass key={i} className="animate-pulse-slow">
                                <div className="flex justify-between items-center">
                                    <Skeleton width="120px" height="12px" />
                                    <Skeleton width="100px" height="12px" />
                                </div>
                                <div className="mt-4">
                                    <Skeleton width="80%" height="40px" className="rounded-xl" />
                                </div>
                            </CardGlass>
                        ))}
                    </div>
                ) : events.length === 0 ? (
                    <CardGlass className="py-24 text-center">
                        <div className="flex flex-col items-center gap-4">
                            <Terminal size={48} className="opacity-10 text-primary" />
                            <h3 className="text-xl font-bold text-white">Silencio en la red</h3>
                            <p className="max-w-sm mx-auto" style={{ color: 'var(--text-soft)' }}>No se han registrado eventos para los filtros seleccionados.</p>
                        </div>
                    </CardGlass>
                ) : (
                    <div className="flex flex-col gap-4">
                        {events.map((ev, idx) => {
                            const evType = ev.event_type || ev.type || ev.action || "unknown";
                            const theme = TYPE_THEMES[evType] || 'default';
                            return (
                                <CardGlass key={ev.id || ev._id || idx} className="hover:border-primary/20 transition-all group p-5">
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-white/5 rounded-lg" style={{ color: 'var(--text-muted)' }}>
                                                {tab === 'security' ? <ShieldAlert size={16} /> : <UserCheck size={16} />}
                                            </div>
                                            <Badge status={theme}>{evType.replace('_', ' ')}</Badge>
                                            {(ev.company_id || ev.target_company_id) && (
                                                <span className="text-[10px] font-black uppercase text-primary tracking-widest bg-primary/5 px-2 py-1 rounded">
                                                    {ev.company_id || ev.target_company_id}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>
                                            <Clock size={12} />
                                            {ev.created_at ? new Date(ev.created_at).toLocaleString("es-ES") : "—"}
                                        </div>
                                    </div>

                                    <div className="bg-[#020617]/40 rounded-xl p-4 border border-white/5">
                                        <p className="text-sm font-medium text-white/90 leading-relaxed mb-3">
                                            {ev.detail || ev.reason || ev.message || "Evento de sistema sin descripción detallada."}
                                        </p>

                                        {ev.ip && (
                                            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/5">
                                                <span className="text-[10px] font-black uppercase tracking-wider" style={{ color: 'var(--text-soft)' }}>Origen IP:</span>
                                                <span className="text-[10px] font-mono text-primary">{ev.ip}</span>
                                            </div>
                                        )}
                                    </div>

                                    {ev.metadata && (
                                        <div className="mt-4 bg-black/60 p-4 rounded-xl border border-white/5 overflow-hidden max-h-[150px] overflow-y-auto">
                                            <pre className="text-[10px] font-mono text-cyan-500/80">
                                                {JSON.stringify(ev.metadata, null, 2)}
                                            </pre>
                                        </div>
                                    )}
                                </CardGlass>
                            );
                        })}

                        {nextCursor && (
                            <div className="pt-8 flex justify-center">
                                <Button
                                    variant="secondary"
                                    onClick={() => load(false)}
                                    disabled={loadingMore}
                                    className="flex items-center gap-2 px-10"
                                >
                                    {loadingMore ? 'Sincronizando...' : 'Cargar más eventos'} <ChevronRight size={16} />
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </SuperAdminLayout>
    );
}
